import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Loader2, RefreshCcw, Search, ListFilter, ChevronDownIcon, LayoutGrid, List } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.trackyourwheelz.com/api';
const DEFAULT_FILTERS = {
  name: '',
  contactNumber: '',
  from: '',
  to: '',
  perPage: '20'
};

const ITEMS_PER_PAGE = 10;
const fieldClassName =
  'h-12 rounded-xl border-1 border-muted-foreground/20 bg-background px-4 text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/20';

const DatePickerField = ({ id, label, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const parsedDate = value ? new Date(value) : undefined;

  return (
    <div className="space-y-2.5">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id={id}
            type="button"
            className={`w-full justify-between font-normal text-left ${fieldClassName}`}
          >
            {parsedDate
              ? parsedDate.toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
              : 'Select date'}
            <ChevronDownIcon className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={parsedDate}
            captionLayout="dropdown-buttons"
            onSelect={(date) => {
              if (date) {
                onChange(format(date, 'yyyy-MM-dd'));
              } else {
                onChange('');
              }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const ReportPage = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [formFilters, setFormFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');

  const fetchCallbackRequests = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        search:
          filters.name?.trim() ||
          filters.contactNumber?.trim() ||
          '',
        from: filters.from || '',
        to: filters.to || '',
        perPage: filters.perPage || '20'
      });

      const response = await fetch(`${API_BASE_URL}/callback-requests?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      

      const data = await response.json();

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.message || 'Failed to load callback requests');
      }

      setRequests(Array.isArray(data.data) ? data.data : []);
      setMeta({
        currentPage: data.meta?.currentPage || 1,
        perPage: data.meta?.perPage || Number(filters.perPage) || 20,
        total: data.meta?.total || (Array.isArray(data.data) ? data.data.length : 0),
        lastPage: data.meta?.lastPage || 1
      });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    if (token) {
      fetchCallbackRequests();
    }
  }, [fetchCallbackRequests, token]);

  const filteredRequests = useMemo(() => {
    const fromDate = filters.from ? new Date(filters.from) : null;
    const toDate = filters.to ? new Date(filters.to) : null;

    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return requests.filter((req) => {
      const requestName = (req.name || '').toLowerCase();
      const requestContact = (req.contact_number || '').toLowerCase();
      const matchesName = filters.name
        ? requestName.includes(filters.name.toLowerCase())
        : true;
      const matchesContact = filters.contactNumber
        ? requestContact.includes(filters.contactNumber.toLowerCase())
        : true;

      if (!matchesName || !matchesContact) {
        return false;
      }

      if (!filters.from && !filters.to) {
        return true;
      }

      const createdAt = req.created_at ? new Date(req.created_at) : null;
      if (!createdAt) return false;

      if (fromDate && createdAt < fromDate) return false;
      if (toDate && createdAt > toDate) return false;

      return true;
    });
  }, [filters, requests]);

  const summary = useMemo(() => {
    const total = filteredRequests.length;
    const latestRequest = filteredRequests.length > 0 ? filteredRequests[0] : null;
    const topicCounts = filteredRequests.reduce((acc, req) => {
      const key = (req.related_to || 'Unspecified').trim() || 'Unspecified';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topTopic = Object.entries(topicCounts).sort((a, b) => b[1] - a[1])[0];

    const today = new Date().toDateString();
    const requestsToday = filteredRequests.filter(
      (req) => new Date(req.created_at).toDateString() === today
    ).length;

    return {
      total,
      latestName: latestRequest?.name || '—',
      latestTime: latestRequest?.created_at || null,
      requestsToday,
      topTopic: topTopic ? topTopic[0] : '—'
    };
  }, [filteredRequests]);

  const handleFilterChange = (field, value) => {
    setFormFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = (event) => {
    event?.preventDefault();
    setFilters(formFilters);
  };

  const handleResetFilters = () => {
    setFormFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredRequests, currentPage]);

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return value;
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl overflow-x-hidden p-4 sm:p-6">
      <div className="space-y-6">
        <div className="rounded-3xl border-1 border-muted-foreground/20 bg-gradient-to-br from-card via-card to-muted/20 p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Analytics Console</p>
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Callback Reports</h1>
              <p className="text-sm text-muted-foreground">Fully redesigned reporting view with premium layout and cleaner insights.</p>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fetchCallbackRequests}
                disabled={loading || !token}
                className="h-10 rounded-xl border-1 border-muted-foreground/20 px-4 text-muted-foreground"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                Refresh
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
                className="h-10 rounded-xl border-1 border-muted-foreground/20 px-4 text-muted-foreground"
              >
                {viewMode === 'table' ? <LayoutGrid className="mr-2 h-4 w-4" /> : <List className="mr-2 h-4 w-4" />}
                {viewMode === 'table' ? 'Grid' : 'Table'}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Card className="min-w-0 rounded-xl border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Requests</p>
              <p className="mt-1.5 text-2xl font-bold text-foreground">{summary.total}</p>
              <p className="mt-1 text-xs text-muted-foreground">Across applied filters</p>
            </CardContent>
          </Card>
          <Card className="min-w-0 rounded-xl border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Latest Request</p>
              <p className="mt-1.5 break-words text-sm font-semibold text-foreground">{summary.latestName}</p>
              <p className="mt-1 text-xs text-muted-foreground">{summary.latestTime ? formatDateTime(summary.latestTime) : 'Waiting for first entry'}</p>
            </CardContent>
          </Card>
          <Card className="min-w-0 rounded-xl border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Requests Today</p>
              <p className="mt-1.5 text-2xl font-bold text-foreground">{summary.requestsToday}</p>
              <p className="mt-1 text-xs text-muted-foreground">Last 24 hours</p>
            </CardContent>
          </Card>
          <Card className="min-w-0 rounded-xl border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Top Interest</p>
              <p className="mt-1.5 break-words text-sm font-semibold text-foreground">{summary.topTopic}</p>
              <p className="mt-1 text-xs text-muted-foreground">Most requested category</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 xl:grid-cols-[340px_1fr]">
          <Card className="h-fit rounded-2xl border-1 border-muted-foreground/20 bg-card shadow-sm xl:sticky xl:top-5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <ListFilter className="h-4 w-4 text-muted-foreground" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleApplyFilters}>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    
                    <Input
                      id="name"
                      placeholder="Search by name"
                      value={formFilters.name}
                      onChange={(e) => handleFilterChange('name', e.target.value)}
                      className={`pl-9 ${fieldClassName}`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactNumber">Contact Number</Label>
                  <div className="relative">
                   
                    <Input
                      id="contactNumber"
                      placeholder="Search by contact number"
                      value={formFilters.contactNumber}
                      onChange={(e) => handleFilterChange('contactNumber', e.target.value)}
                      className={`pl-9 ${fieldClassName}`}
                    />
                  </div>
                </div>
                <div className="rounded-xl border-1 border-muted-foreground/10 bg-muted/10 p-3">
                  <DatePickerField id="from" label="From Date" value={formFilters.from} onChange={(value) => handleFilterChange('from', value)} />
                </div>
                <div className="rounded-xl border-1 border-muted-foreground/10 bg-muted/10 p-3">
                  <DatePickerField id="to" label="To Date" value={formFilters.to} onChange={(value) => handleFilterChange('to', value)} />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="ghost" className="h-10 flex-1 rounded-xl" onClick={handleResetFilters} disabled={loading}>
                    Reset
                  </Button>
                  <Button type="submit" variant="outline" className="h-10 flex-1 rounded-xl border-1 border-muted-foreground/20" disabled={loading}>
                    Apply
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-1 border-muted-foreground/20 bg-card shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Callback Request Feed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">{error}</div>}

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : paginatedRequests.length === 0 ? (
                <div className="rounded-xl border-1 border-muted-foreground/20 bg-muted/20 px-4 py-16 text-center text-sm text-muted-foreground">
                  No callback requests found for the selected filters.
                </div>
              ) : viewMode === 'table' ? (
                <div className="overflow-x-hidden rounded-xl border-1 border-muted-foreground/20">
                  <Table className="w-full table-fixed">
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead className="w-[18%] min-w-[120px]">Name</TableHead>
                        <TableHead className="w-[18%] min-w-[120px]">Contact</TableHead>
                        <TableHead className="w-[20%] min-w-[130px]">Related To</TableHead>
                        <TableHead className="w-[28%] min-w-[180px]">Description</TableHead>
                        <TableHead className="w-[16%] min-w-[140px]">Requested On</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRequests.map((request) => (
                        <TableRow key={request.id} className="hover:bg-muted/20">
                          <TableCell className="font-semibold text-foreground">
                            <p className="truncate">{request.name || '—'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="truncate text-sm text-foreground">{request.contact_number || '—'}</p>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="max-w-full truncate capitalize border-1 border-muted-foreground/20"
                              title={request.related_to || 'Unspecified'}
                            >
                              {request.related_to || 'Unspecified'}
                            </Badge>
                          </TableCell>
                          <TableCell className="align-top">
                            <p
                              className="max-w-full overflow-hidden text-ellipsis break-words text-sm leading-5 text-muted-foreground line-clamp-2"
                              title={request.description || '—'}
                            >
                              {request.description || '—'}
                            </p>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <p className="truncate" title={formatDateTime(request.created_at)}>
                              {formatDateTime(request.created_at)}
                            </p>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {paginatedRequests.map((request) => (
                    <div key={request.id} className="rounded-2xl border-1 border-muted-foreground/20 bg-muted/20 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Name</p>
                          <p className="truncate text-sm font-semibold text-foreground">{request.name || 'N/A'}</p>
                        </div>
                        <Badge variant="outline" className="capitalize border-1 border-muted-foreground/20">
                          {request.related_to || 'Unspecified'}
                        </Badge>
                      </div>
                      <div className="mt-3 space-y-2 text-sm">
                        <p className="text-foreground"><span className="text-muted-foreground">Contact:</span> {request.contact_number || '—'}</p>
                        <p className="text-foreground"><span className="text-muted-foreground">Requested On:</span> {formatDateTime(request.created_at)}</p>
                        <p className="text-muted-foreground line-clamp-2">{request.description || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-3 rounded-xl border-1 border-muted-foreground/20 bg-muted/10 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-muted-foreground">
                  Showing {filteredRequests.length} of {meta.total || requests.length} requests
                </div>
                <div className="text-xs text-muted-foreground">
                  Page {currentPage} of {totalPages} • {ITEMS_PER_PAGE} per page
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="rounded-xl border-1 border-muted-foreground/20"
                >
                  Previous
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) =>
                    p === 1 ||
                    p === totalPages ||
                    Math.abs(p - currentPage) <= 1
                  )
                  .map((page, idx, arr) => (
                    <React.Fragment key={page}>
                      {idx > 0 && page - arr[idx - 1] > 1 ? (
                        <span className="px-1 text-muted-foreground">...</span>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={
                          currentPage === page
                            ? '!bg-black !text-white dark:!bg-white dark:!text-black border-black dark:border-white rounded-xl'
                            : 'rounded-xl border-1 border-muted-foreground/20'
                        }
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="rounded-xl border-1 border-muted-foreground/20"
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;